import { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Home } from './pages/Home';
import { AuthCallback } from './pages/AuthCallback';
import { Privacy } from './pages/Privacy';
import { PrivacyKo } from './pages/PrivacyKo';
import { Terms } from './pages/Terms';
import { TermsKo } from './pages/TermsKo';
import { Contact } from './pages/Contact';
import { useAuthStore } from './stores/authStore';
import { EnvironmentBadge } from './components/ui/EnvironmentBadge';
import { ErrorBoundary } from './components/ErrorBoundary';
// import { FeedbackButton } from './components/ui/FeedbackButton';
import { UserOnboarding } from './components/onboarding/UserOnboarding';

// Lazy load heavy pages
const DashboardV2 = lazy(() => import('./pages/DashboardV2').then(m => ({ default: m.DashboardV2 })));
const FitReplayCreate = lazy(() => import('./pages/FitReplayCreate').then(m => ({ default: m.FitReplayCreate })));
const StravaReplayCreate = lazy(() => import('./pages/StravaReplayCreate').then(m => ({ default: m.StravaReplayCreate })));
const Login = lazy(() => import('./pages/Login').then(m => ({ default: m.Login })));
const Register = lazy(() => import('./pages/Register').then(m => ({ default: m.Register })));
const Profile = lazy(() => import('./pages/Profile').then(m => ({ default: m.Profile })));
const ShareRedirect = lazy(() => import('./pages/ShareRedirect').then(m => ({ default: m.ShareRedirect })));
const OpenContributions = lazy(() => import('./pages/OpenContributions').then(m => ({ default: m.OpenContributions })));
const OpenEventCreate = lazy(() => import('./pages/OpenEventCreate'));
const Activities = lazy(() => import('./pages/Activities'));

// Design Variants (Disabled - WIP, lint errors)
// const HomeVariantA = lazy(() => import('./pages/design/HomeVariantA').then(m => ({ default: m.HomeVariantA })));
// const HomeVariantB = lazy(() => import('./pages/design/HomeVariantB').then(m => ({ default: m.HomeVariantB })));
// const HomeVariantC = lazy(() => import('./pages/design/HomeVariantC').then(m => ({ default: m.HomeVariantC })));
// const HomeVariantD = lazy(() => import('./pages/design/HomeVariantD').then(m => ({ default: m.HomeVariantD })));
// const HomeVariantE = lazy(() => import('./pages/design/HomeVariantE').then(m => ({ default: m.HomeVariantE })));
// const HomeVariantF = lazy(() => import('./pages/design/HomeVariantF').then(m => ({ default: m.HomeVariantF })));

function App() {
  const { initialize, isInitializing } = useAuthStore();

  useEffect(() => {
    initialize();
  }, [initialize]);

  if (isInitializing) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: 'var(--color-bg-primary)' }}
      >
        <div
          className="w-8 h-8 border-4 border-t-transparent rounded-full animate-spin"
          style={{ borderColor: 'var(--color-accent-red)', borderTopColor: 'transparent' }}
        />
      </div>
    );
  }

  const PageLoader = (
    <div
      className="min-h-screen flex items-center justify-center"
      style={{ backgroundColor: 'var(--color-bg-primary)' }}
    >
      <div
        className="w-8 h-8 border-4 border-t-transparent rounded-full animate-spin"
        style={{ borderColor: 'var(--color-accent-red)', borderTopColor: 'transparent' }}
      />
    </div>
  );

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <UserOnboarding />
        <EnvironmentBadge />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/event/:eventId" element={<Suspense fallback={PageLoader}><DashboardV2 /></Suspense>} />
          <Route path="/login" element={<Suspense fallback={PageLoader}><Login /></Suspense>} />
          <Route path="/register" element={<Suspense fallback={PageLoader}><Register /></Suspense>} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/profile" element={<Suspense fallback={PageLoader}><Profile /></Suspense>} />
          {/* Redirects from old URLs to unified profile page */}
          <Route path="/my-events" element={<Navigate to="/profile?tab=events" replace />} />
          <Route path="/my-devices" element={<Navigate to="/profile?tab=devices" replace />} />
          <Route path="/liked-replays" element={<Navigate to="/profile?tab=favorites" replace />} />
          <Route path="/fit-replay/create" element={<Suspense fallback={PageLoader}><FitReplayCreate /></Suspense>} />
          <Route path="/strava-replay/create" element={<Suspense fallback={PageLoader}><StravaReplayCreate /></Suspense>} />
          <Route path="/open-event/create" element={<Suspense fallback={PageLoader}><OpenEventCreate /></Suspense>} />
          <Route path="/open-contributions" element={<Suspense fallback={PageLoader}><OpenContributions /></Suspense>} />
          <Route path="/activities" element={<Suspense fallback={PageLoader}><Activities /></Suspense>} />
          <Route path="/s/:code" element={<Suspense fallback={PageLoader}><ShareRedirect /></Suspense>} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/privacy/ko" element={<PrivacyKo />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/terms/ko" element={<TermsKo />} />
          <Route path="/contact" element={<Contact />} />
          
          {/* Design Review Routes (Disabled - WIP)
          <Route path="/design/home-a" element={<Suspense fallback={PageLoader}><HomeVariantA /></Suspense>} />
          <Route path="/design/home-b" element={<Suspense fallback={PageLoader}><HomeVariantB /></Suspense>} />
          <Route path="/design/home-c" element={<Suspense fallback={PageLoader}><HomeVariantC /></Suspense>} />
          <Route path="/design/home-d" element={<Suspense fallback={PageLoader}><HomeVariantD /></Suspense>} />
          <Route path="/design/home-e" element={<Suspense fallback={PageLoader}><HomeVariantE /></Suspense>} />
          <Route path="/design/home-f" element={<Suspense fallback={PageLoader}><HomeVariantF /></Suspense>} />
          */}
        </Routes>
        {/* <FeedbackButton /> */}
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
